import { Injectable } from '@angular/core';
import { TaggableDTO } from './api/taggable';
import { API, NamedIdentityDTO, TagEntityDTO } from './api/api';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ColorPickerService } from './color-picker.service';
import { UtilService } from './util.service';
import { PermissionService } from './permission.service';
import { map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TagService {
  public TaggableDTO = TaggableDTO;
  tagViewOpen: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  constructor(
    private readonly api: API,
    public readonly colorService: ColorPickerService,
    public readonly util: UtilService,
    public readonly permissionService: PermissionService
  ) {
    if (this.permissionService.hasPermission(this.permissionService.Permission.color_definition_list)) {
      this.colorService.getColorSet();
    }
  }

  getAndAddTagEntities<T extends NamedIdentityDTO>(
    linkedEntityType: string,
    dtoElements: T[]
  ): (T & {
    tags?: TagEntityDTO[];
  })[] {
    if (!dtoElements || dtoElements.length === 0) {
      return [];
    }
    const linkedEntityIds: number[] = dtoElements.map(element => element.id);
    this.api
      .shared_Tag_getTagEntitiesInBulkGroupByLinkedEntityId(linkedEntityType, { linkedEntity_id: linkedEntityIds })
      .pipe(take(1))
      .subscribe(tags => {
        if (Object.keys(tags).length === 0) {
          return dtoElements;
        }
        for (const dtoElement of dtoElements) {
          // add TagEntity Array / undefined to the current element
          Object.assign(dtoElement, { tags: tags[dtoElement.id] });
        }
        return dtoElements;
      });
    return dtoElements;
  }

  getAndAddTagEntitiesObs<T extends NamedIdentityDTO>(
    linkedEntityType: string,
    dtoElements: T[]
  ): Observable<
    (T & {
      tags?: TagEntityDTO[];
    })[]
  > {
    // no elements provided / length === 0
    if (!dtoElements || dtoElements.length === 0) {
      return of([]);
    }
    //has no tag list permission, just return input elements
    if (!this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
      return of(dtoElements);
    }
    // has elements and permission to retrieve Tags
    const linkedEntityIds: number[] = dtoElements.map(element => element.id);
    return this.api.shared_Tag_getTagEntitiesInBulkGroupByLinkedEntityId(linkedEntityType, { linkedEntity_id: linkedEntityIds }).pipe(
      take(1),
      map(tags => {
        if (Object.keys(tags).length === 0) {
          return dtoElements;
        }
        return dtoElements.map(dtoElement => {
          return { ...dtoElement, tags: tags[dtoElement.id] };
        });
      })
    );
  }
}
